/**
 * Global keyboard shortcuts for the chat surface, wired once in ChatPage.
 *
 * Keymap (the same map is rendered by ShortcutsHint so it stays discoverable):
 *   - Cmd/Ctrl+Shift+O  → start a new conversation (reset draft + active id)
 *   - Cmd/Ctrl+L or "/" → focus the composer textarea
 *   - Esc → context-sensitive: STOP the in-flight generation while streaming;
 *           otherwise CLOSE the artifacts side panel if it is open.
 *
 * Esc precedence (single source of truth): streaming wins. We deliberately
 * handle BOTH Esc behaviours in this one global listener rather than adding a
 * second competing keydown handler in the panel — two listeners would race and
 * an Esc could both stop a stream and close the panel in the same keystroke.
 * Order: if streaming → stop (and do NOT also close the panel); else if the
 * panel is open → close it.
 *
 * Typing guard: we do NOT hijack keys while the user is typing in a text field
 * (input / textarea / contenteditable) — EXCEPT Esc, which must always be able
 * to stop a stream even with the composer focused. The "/" focus shortcut is the
 * classic example: without the guard it would be impossible to type a literal
 * "/" into the composer.
 */
import { useEffect } from 'react';
import { useChat } from '@/features/chat/data-access/useChat';
import { useSessionStore } from '@/features/chat/store/sessionStore';

/** The data-testid the composer textarea renders (see Composer.tsx). */
const COMPOSER_TESTID = 'chat-composer-input';

/** True when the event originated from an editable field. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable === true
  );
}

export function useKeyboardShortcuts() {
  const { cancel, isStreaming } = useChat();
  const reset = useSessionStore((s) => s.reset);
  const setActiveConversation = useSessionStore((s) => s.setActiveConversation);
  const artifactOpen = useSessionStore((s) => s.artifactPanel.open);
  const closeArtifact = useSessionStore((s) => s.closeArtifact);

  useEffect(() => {
    const focusComposer = () => {
      const el = document.querySelector<HTMLTextAreaElement>(
        `[data-testid="${COMPOSER_TESTID}"]`,
      );
      el?.focus();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const typing = isTypingTarget(e.target);

      // Esc is context-sensitive. Precedence: streaming-stop ALWAYS wins (even
      // while typing in the composer — you must be able to abort a stream).
      // Otherwise, close the artifacts panel — but ONLY if this Esc wasn't
      // already handled by a local control (e.g. the sidebar rename input's own
      // Esc-to-cancel, which calls preventDefault) and the focus isn't in a text
      // field. (Per the 2026-06-20 codex review, finding #7.)
      if (e.key === 'Escape') {
        if (isStreaming) {
          e.preventDefault();
          cancel();
        } else if (artifactOpen && !e.defaultPrevented && !typing) {
          e.preventDefault();
          closeArtifact();
        }
        return;
      }

      // New conversation: Cmd/Ctrl+Shift+O.
      if (mod && e.shiftKey && (e.key === 'o' || e.key === 'O')) {
        e.preventDefault();
        reset();
        setActiveConversation(null);
        return;
      }

      // Focus composer: Cmd/Ctrl+L.
      if (mod && !e.shiftKey && (e.key === 'l' || e.key === 'L')) {
        e.preventDefault();
        focusComposer();
        return;
      }

      // Focus composer: bare "/" — but NOT while already typing (else you could
      // never type a literal slash).
      if (e.key === '/' && !mod && !typing) {
        e.preventDefault();
        focusComposer();
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    cancel,
    isStreaming,
    reset,
    setActiveConversation,
    artifactOpen,
    closeArtifact,
  ]);
}

/** The shortcut keymap, exported so the discoverability hint and tests share it. */
export const SHORTCUTS: ReadonlyArray<{ keys: string; label: string }> = [
  { keys: '⌘/Ctrl + Shift + O', label: 'New conversation' },
  { keys: '⌘/Ctrl + L  or  /', label: 'Focus composer' },
  { keys: 'Esc', label: 'Stop generating / close panel' },
];
